import { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import Card from '../../../components/card/Card';
import Button from '../../../components/button/Button';
import Spinner from '../../../components/Spinner/spinner';
import { useToast } from '../../../components/toast/toast';
import { getProductLines } from '../../../services/ClientService';
import {
  getCreditoMaximo,
  calcularCreditoMaximo,
  saveCreditoOtorgado,
  addProductToApplication,
  deleteProductFromApplication,
} from '../../../services/RiskService';
import { getQuotations, Quotation } from '../../../services/QuotationsService';
import { applicationData } from '../../../types/interfaces';
import { isSuccessfulResponse, hasError } from '../../../utils/Utils';
import {
  computeMontoOtorgadoString,
  hasCreditoMaximoUnitSale,
  mapSaleValueFromCreditoMaximoApi,
  parseCreditoMaximoUnitSale,
  resolveUnitSaleForProduct,
} from '../../../utils/creditoMaximoMonto';
import {
  computeSuperaMaximo,
  resolveQuotationValues,
} from '../../../utils/creditoMaximoSupera';
import {
  defaultSelectableProductLineCode,
  parseSelectableProductLines,
  type SelectableProductLine,
} from '../../../utils/productLines';
import {
  CreditoMaximoState,
  ProductoOtorgado,
} from './CreditoMaximo.types';
import ActivoFijoLibreForm from './ActivoFijoLibreForm';
import MontoAOtorgar from './MontoAOtorgar';
import { useConfig } from '../../../context/ConfigContext';
import {
  hasHistoricUsdProducts,
  isUsdHistoricLocked,
  isUsdSelectableForOriginationCurrencyDropdown,
  resolveLocalCurrencyCode,
  resolveUsdAvailable,
} from '../../../utils/quotationAssets';
import {
  getManagementAssetRules,
  getMissingQuotationMessages,
} from '../../../utils/managementAssetRules';

export interface CreditoMaximoSectionProps {
  cuit: string;
  applicationId: string;
  products: applicationData[];
  isQuotationsEnabled?: boolean;
  hideSaveButton?: boolean;
  onSavingChange?: (isSaving: boolean) => void;
  managementCode?: string | null;
  /** Moneda nacional (ARS por defecto; UYU en Cementos Uruguay). #52786 */
  localCurrencyCode?: string;
}

export interface CreditoMaximoSectionHandle {
  save: () => Promise<boolean>;
}

const initProductosFromApplicationData = (
  products: applicationData[],
  localCurrencyCode: string
): ProductoOtorgado[] =>
  products.map((p) => ({
    product_id: p.id,
    product_line_code: p.product_line_code,
    asset_code: p.asset_code,
    asset_name: p.asset_name,
    cantidad_otorgada: p.asset_count?.toString() ?? '0',
    monto_otorgado: p.amount?.value?.toString() ?? '0',
    currency_code: p.amount?.currency_code ?? localCurrencyCode,
  }));

const mapProductoFromApi = (
  p: Record<string, unknown>,
  localCurrencyCode: string
): ProductoOtorgado => {
  const sv = mapSaleValueFromCreditoMaximoApi(p);
  // POST /products devuelve ApplicationProductResponse con `id`; GET crédito máximo usa `productId`.
  const rawProductId = p.productId ?? p.product_id ?? p.id;
  return {
    product_id: rawProductId != null && rawProductId !== '' ? Number(rawProductId) : 0,
    product_line_code: String(p.productLineCode ?? p.product_line_code ?? ''),
    asset_code: p.assetCode != null ? String(p.assetCode) : p.asset_code != null ? String(p.asset_code) : undefined,
    asset_name: p.assetName != null ? String(p.assetName) : p.asset_name != null ? String(p.asset_name) : undefined,
    cantidad_otorgada: String(p.cantidadOtorgada ?? p.cantidad_otorgada ?? '0'),
    monto_otorgado: String(p.montoOtorgado ?? p.monto_otorgado ?? '0'),
    currency_code: String(p.currencyCode ?? p.currency_code ?? localCurrencyCode),
    ...(sv != null ? { saleValue: sv } : {}),
  };
};

/** Monto de línea > 0 (acepta coma decimal como en otros flujos). */
const montoLineaEsMayorQueCero = (raw: string): boolean => {
  const normalized = String(raw).trim().replace(',', '.');
  const n = parseFloat(normalized);
  return Number.isFinite(n) && n > 0;
};

const EMPTY_STATE: CreditoMaximoState = {
  activo_fijo: 0,
  revaluos: '0',
  restricciones: '0',
  activo_fijo_libre: 0,
  credito_maximo_unidades: 0,
  credito_maximo_pesos: 0,
  calificacion: null,
  productos: [],
  superaMaximo: false,
  unidad_calculo_code: 'BOL',
};

const CreditoMaximoSection = forwardRef<CreditoMaximoSectionHandle, CreditoMaximoSectionProps>(({
  cuit,
  applicationId,
  products,
  isQuotationsEnabled = false,
  hideSaveButton = false,
  onSavingChange,
  managementCode = null,
  localCurrencyCode: localCurrencyCodeProp,
}, ref) => {
  const config = useConfig();
  const localCurrencyCode =
    localCurrencyCodeProp ?? resolveLocalCurrencyCode(config);
  const [state, setState] = useState<CreditoMaximoState>({
    ...EMPTY_STATE,
    productos: initProductosFromApplicationData(products, localCurrencyCode),
  });
  const [productLines, setProductLines] = useState<SelectableProductLine[]>([]);
  const [availableAssets, setAvailableAssets] = useState<Array<{ code: string; name: string; saleValue?: number | string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const productsRef = useRef(products);
  productsRef.current = products;

  const updateSaving = (v: boolean) => {
    setIsSaving(v);
    onSavingChange?.(v);
  };

  useEffect(() => {
    getProductLines().then((res) => {
      if (res && 'data' in res) setProductLines(parseSelectableProductLines(res.data));
    });
  }, []);

  useEffect(() => {
    if (isQuotationsEnabled) {
      getQuotations()
        .then((res) => {
          const assets: Array<{ code: string; name: string; saleValue?: number | string }> =
            Array.isArray(res?.data)
              ? res.data.map((q: Quotation) => ({ code: q.code, name: q.name, saleValue: q.saleValue }))
              : [];
          setAvailableAssets(assets);
        })
        .catch(() => setAvailableAssets([]));
    }
  }, [isQuotationsEnabled]);

  /** Evita re-ejecutar GET credimax en loop cuando el padre pasa un array nuevo por render con el mismo contenido. */
  const applicationProductsKey = useMemo(
    () =>
      products
        .map(
          (p) =>
            `${p.id ?? ''}-${p.product_line_code}-${p.amount?.value ?? ''}-${p.amount?.currency_code ?? ''}`
        )
        .join('|'),
    [products]
  );

  const mergedAvailableAssets = useMemo(() => {
    const map = new Map<string, { code: string; name: string; saleValue?: number | string }>();
    for (const a of availableAssets) map.set(a.code, { ...a });
    for (const p of state.productos) {
      if (p.asset_code && p.saleValue != null && p.saleValue > 0) {
        const cur = map.get(p.asset_code);
        if (!cur || !hasCreditoMaximoUnitSale(cur.saleValue)) {
          map.set(p.asset_code, {
            code: p.asset_code,
            name: p.asset_name ?? p.asset_code,
            saleValue: p.saleValue,
          });
        }
      }
    }
    return Array.from(map.values());
  }, [availableAssets, state.productos]);

  const usdQuotationAvailable = useMemo(() => {
    if (!isQuotationsEnabled) return null;
    return isUsdSelectableForOriginationCurrencyDropdown(
      { totalElements: availableAssets.some((a) => a.code === 'USD') ? 1 : 0 },
      availableAssets
    );
  }, [isQuotationsEnabled, availableAssets]);

  const usdAvailable = useMemo(
    () =>
      resolveUsdAvailable(
        {
          isQuotationsEnabled,
          flagUsdCurrencyEnabled: config.flagUsdCurrencyEnabled,
        },
        usdQuotationAvailable
      ),
    [isQuotationsEnabled, config.flagUsdCurrencyEnabled, usdQuotationAvailable]
  );

  const usdHistoricLocked = useMemo(
    () => isUsdHistoricLocked(usdAvailable, hasHistoricUsdProducts(state.productos)),
    [usdAvailable, state.productos]
  );

  useEffect(() => {
    setIsLoading(true);
    getCreditoMaximo(cuit, applicationId)
      .then((res) => {
        if (isSuccessfulResponse(res) && res.data) {
          const data = res.data as Record<string, unknown>;
          const productosRaw = data.productos as Record<string, unknown>[] | undefined;
          const productosFromApi =
            Array.isArray(productosRaw) && productosRaw.length > 0
              ? productosRaw.map((p) => mapProductoFromApi(p, localCurrencyCode))
              : initProductosFromApplicationData(productsRef.current, localCurrencyCode);
          setState({
            activo_fijo: Number(data.activoFijo ?? data.activo_fijo ?? 0),
            revaluos: String(data.revaluos ?? '0'),
            restricciones: String(data.restricciones ?? '0'),
            activo_fijo_libre: Number(data.activoFijoLibre ?? data.activo_fijo_libre ?? 0),
            // Lectura defensiva: nuevo nombre primero, fallback al nombre anterior (compatibilidad 1 release)
            credito_maximo_unidades: Number(data.creditoMaximoUnidades ?? data.creditoMaximoBolsas ?? data.credito_maximo_unidades ?? data.credito_maximo_bolsas ?? 0),
            credito_maximo_pesos: Number(data.creditoMaximoPesos ?? data.credito_maximo_pesos ?? 0),
            calificacion: (data.calificacion as string | null | undefined) ?? null,
            superaMaximo: data.superaMaximo === true || data.supera_maximo === true,
            productos: productosFromApi,
            unidad_calculo_code: String(data.unidadCalculoCode ?? data.unidad_calculo_code ?? 'BOL'),
          });
        }
      })
      .finally(() => setIsLoading(false));
    // `applicationProductsKey` evita re-fetch en loop cuando el padre recrea el array `products` en cada render.
    // `productsRef` mantiene la solicitud actual para el fallback sin depender de la referencia del array.
  }, [cuit, applicationId, applicationProductsKey, localCurrencyCode]);

  const handleRecalcular = useCallback(async () => {
    setIsRecalculating(true);
    const res = await calcularCreditoMaximo(cuit, applicationId, {
      revaluos: parseFloat(state.revaluos) || 0,
      restricciones: parseFloat(state.restricciones) || 0,
    });
    if (isSuccessfulResponse(res) && res.data) {
      const d = res.data as Record<string, unknown>;
      // La API devuelve activoFijo en GET y POST calcular; si el GET inicial falló antes de persistir PPE, hay que tomarlo del recálculo.
      setState((prev) => ({
        ...prev,
        activo_fijo: Number(d.activoFijo ?? d.activo_fijo ?? prev.activo_fijo),
        revaluos: d.revaluos != null ? String(d.revaluos) : prev.revaluos,
        restricciones: d.restricciones != null ? String(d.restricciones) : prev.restricciones,
        activo_fijo_libre: Number(d.activoFijoLibre ?? d.activo_fijo_libre ?? prev.activo_fijo_libre),
        // Lectura defensiva: nuevo nombre primero, fallback al nombre anterior (compatibilidad 1 release)
        credito_maximo_unidades: Number(d.creditoMaximoUnidades ?? d.creditoMaximoBolsas ?? d.credito_maximo_unidades ?? d.credito_maximo_bolsas ?? prev.credito_maximo_unidades),
        credito_maximo_pesos: Number(d.creditoMaximoPesos ?? d.credito_maximo_pesos ?? prev.credito_maximo_pesos),
        calificacion: (d.calificacion as string | null | undefined) ?? prev.calificacion,
        superaMaximo: d.superaMaximo === true || d.supera_maximo === true,
        unidad_calculo_code: String(d.unidadCalculoCode ?? d.unidad_calculo_code ?? prev.unidad_calculo_code ?? 'BOL'),
      }));
    }
    setIsRecalculating(false);
  }, [cuit, applicationId, state.revaluos, state.restricciones]);

  const handleGuardar = useCallback(async (): Promise<boolean> => {
    if (state.productos.length === 0) {
      useToast.error({
        title: 'Agregá un producto para poder avanzar',
        id: 'risk-toast',
        toastId: 'risk-toast-credito-sin-productos',
        description: '',
        closeButton: false,
        autoClose: 4000,
      });
      return false;
    }
    if (state.productos.some((p) => !montoLineaEsMayorQueCero(p.monto_otorgado))) {
      useToast.error({
        title: 'indicá un monto mayor a cero en todas las líneas.',
        id: 'risk-toast',
        toastId: 'risk-toast-credito-monto-cero',
        description: '',
        closeButton: false,
        autoClose: 4000,
      });
      return false;
    }
    updateSaving(true);
    const res = await saveCreditoOtorgado(cuit, applicationId, {
      revaluos: parseFloat(state.revaluos) || 0,
      restricciones: parseFloat(state.restricciones) || 0,
      productos: state.productos.map((p) => {
        const base = {
          product_id: p.product_id,
          cantidad_otorgada: p.cantidad_otorgada,
          monto_otorgado: p.monto_otorgado,
          product_line_code: p.product_line_code,
          currency_code: p.currency_code,
        };
        return isQuotationsEnabled
          ? { ...base, asset_code: p.asset_code ?? '' }
          : base;
      }),
    });
    if (isSuccessfulResponse(res) && res.data) {
      const d = res.data as Record<string, unknown>;
      setState((prev) => ({
        ...prev,
        superaMaximo: d.superaMaximo === true || d.supera_maximo === true,
      }));
      useToast.success({
        title: 'Crédito otorgado guardado correctamente.',
        id: 'risk-toast',
        description: '',
        closeButton: false,
        autoClose: 4000,
      });
      updateSaving(false);
      return true;
    } else if (hasError(res).isAnyError) {
      useToast.error({
        title: 'Error al guardar. Intentá nuevamente.',
        id: 'risk-toast',
        description: '',
        closeButton: false,
        autoClose: 4000,
      });
    }
    updateSaving(false);
    return false;
  }, [cuit, applicationId, state.revaluos, state.restricciones, state.productos, isQuotationsEnabled]);

  useImperativeHandle(ref, () => ({ save: handleGuardar }), [handleGuardar]);

  const handleProductoChange = (
    productId: number,
    field: 'product_line_code' | 'asset_code' | 'asset_name' | 'currency_code' | 'cantidad_otorgada' | 'monto_otorgado',
    value: string
  ) => {
    let shouldRecalcularMaximo = false;

    setState((prev) => {
      const updatedProductos = prev.productos.map((p) => {
        if (p.product_id !== productId) return p;
        let updated: ProductoOtorgado = { ...p, [field]: value };

        if (!isQuotationsEnabled) return updated;

        if (field === 'asset_code') {
          // Recalcular el crédito máximo si el producto ya tenía un asset_code (está persistido en BD)
          // y se está cambiando a un código diferente.
          const prevAssetCode = p.asset_code;
          if (prevAssetCode && prevAssetCode !== value) {
            shouldRecalcularMaximo = true;
          }

          const sinActivo = !value || String(value).trim() === '';
          const asset = !sinActivo
            ? mergedAvailableAssets.find((a) => a.code === value)
            : undefined;
          const unit = asset ? parseCreditoMaximoUnitSale(asset.saleValue) : null;
          updated = {
            ...updated,
            saleValue: unit != null && unit > 0 ? unit : undefined,
          };
          const count = parseInt(updated.cantidad_otorgada, 10) || 0;
          if (sinActivo) {
            updated.asset_name = '';
            updated.cantidad_otorgada = '0';
            // Misma semántica que "Moneda" en originación: no borrar el monto ingresado a mano.
          } else if (value && unit != null && unit > 0 && count > 0) {
            updated.monto_otorgado = computeMontoOtorgadoString(unit, count);
          } else if (value) {
            updated.monto_otorgado = '0';
          }
          return updated;
        }

        if (field === 'asset_name') {
          return updated;
        }

        if (field === 'cantidad_otorgada') {
          const unit = resolveUnitSaleForProduct(updated, mergedAvailableAssets);
          const count = parseInt(value, 10) || 0;
          if (updated.asset_code) {
            if (unit != null) {
              updated.monto_otorgado =
                count > 0 ? computeMontoOtorgadoString(unit, count) : '0';
            } else {
              updated.monto_otorgado = '0';
            }
          }
          return updated;
        }

        return updated;
      });

      return { ...prev, productos: updatedProductos };
    });

    // Disparar recálculo del crédito máximo tras actualizar el estado, solo cuando el asset_code
    // de un producto existente (ya persistido) cambió. El backend leerá el asset_code actualizado
    // de la BD (persiste en la originación/riesgo) para determinar la unidad correcta.
    if (shouldRecalcularMaximo) {
      handleRecalcular();
    }
  };

  const handleAddProducto = useCallback(async () => {
    const defaultCode = defaultSelectableProductLineCode(productLines);
    if (!defaultCode) {
      useToast.error({
        title: 'No hay productos habilitados para agregar.',
        id: 'risk-toast',
        closeButton: false,
        autoClose: 4000,
      });
      return;
    }
    const res = await addProductToApplication(cuit, applicationId, {
      product_line_code: defaultCode,
      currency_code: localCurrencyCode,
      amount: 0,
    });
    if (isSuccessfulResponse(res) && res.data) {
      const row = mapProductoFromApi(res.data as Record<string, unknown>, localCurrencyCode);
      setState((prev) => ({ ...prev, productos: [...prev.productos, row] }));
    } else if (hasError(res).isAnyError) {
      useToast.error({
        title: 'No se pudo agregar el producto. Intentá nuevamente.',
        id: 'risk-toast',
        closeButton: false,
        autoClose: 4000,
      });
    }
  }, [cuit, applicationId, productLines, localCurrencyCode]);

  const handleDeleteProducto = useCallback(
    async (productId: number) => {
      const res = await deleteProductFromApplication(cuit, applicationId, productId);
      if (isSuccessfulResponse(res)) {
        setState((prev) => ({
          ...prev,
          productos: prev.productos.filter((p) => p.product_id !== productId),
        }));
      } else if (hasError(res).isAnyError) {
        useToast.error({
          title: 'No se pudo eliminar el producto. Intentá nuevamente.',
          id: 'risk-toast',
          closeButton: false,
          autoClose: 4000,
        });
      }
    },
    [cuit, applicationId]
  );

  const superaMaximo = useMemo(() => {
    const { valorBolsa, valorM3 } = resolveQuotationValues(mergedAvailableAssets);
    return computeSuperaMaximo({
      productos: state.productos,
      creditoMaximoUnidades: state.credito_maximo_unidades,
      unidadCalculoCode: state.unidad_calculo_code ?? 'BOL',
      valorBolsa,
      valorM3,
    });
  }, [state.productos, state.credito_maximo_unidades, state.unidad_calculo_code, mergedAvailableAssets]);

  const managementRules = useMemo(
    () => getManagementAssetRules(managementCode),
    [managementCode]
  );

  const hasQuotationConfigErrors = useMemo(() => {
    if (!isQuotationsEnabled || !managementRules) return false;
    return state.productos.some(
      (p) =>
        getMissingQuotationMessages({
          rules: managementRules,
          assetCode: p.asset_code,
          currencyCode: p.currency_code,
          assets: mergedAvailableAssets,
        }).length > 0
    );
  }, [isQuotationsEnabled, managementRules, state.productos, mergedAvailableAssets]);

  if (isLoading) {
    return (
      <Card display='flex' justifyContent='center' margin={{ bottom: '5' }}>
        <Spinner size='md' variant='primary' />
      </Card>
    );
  }

  return (
    <>
      <ActivoFijoLibreForm
        activoFijo={state.activo_fijo}
        revaluos={state.revaluos}
        restricciones={state.restricciones}
        activoFijoLibre={state.activo_fijo_libre}
        creditoMaximoUnidades={state.credito_maximo_unidades}
        creditoMaximoPesos={state.credito_maximo_pesos}
        unidadCalculoCode={state.unidad_calculo_code}
        calificacion={state.calificacion}
        isRecalculating={isRecalculating}
        onRevaluosChange={(value) => setState((prev) => ({ ...prev, revaluos: value }))}
        onRestriccionesChange={(value) => setState((prev) => ({ ...prev, restricciones: value }))}
        onBlur={handleRecalcular}
      />

      <MontoAOtorgar
        productos={state.productos}
        productLines={productLines}
        superaMaximo={superaMaximo}
        onProductoChange={handleProductoChange}
        onDeleteProducto={handleDeleteProducto}
        onAddProducto={handleAddProducto}
        onBlur={handleRecalcular}
        isQuotationsEnabled={isQuotationsEnabled}
        availableAssets={mergedAvailableAssets}
        usdAvailable={usdAvailable}
        isUsdHistoricLocked={usdHistoricLocked}
        managementCode={managementCode}
        localCurrencyCode={localCurrencyCode}
      />

      {!hideSaveButton && (
        <Card display='flex' justifyContent='flex-end' margin={{ bottom: '4' }}>
          <Card width='160px'>
            <Button
              id='credito-maximo-guardar'
              size='lg'
              variant='primary'
              fullWidth
              onClick={handleGuardar}
              disabled={isSaving || hasQuotationConfigErrors}
            >
              {isSaving ? <Spinner size='sm' variant='primary' /> : 'Guardar'}
            </Button>
          </Card>
        </Card>
      )}
    </>
  );
});

CreditoMaximoSection.displayName = 'CreditoMaximoSection';

export default CreditoMaximoSection;
